import { centsToFormattedDollars } from '@pesto/utils';

import type { ApplicantFormData, OpenAccountFormData } from './openAccountSchema';

import { CardPlan, KycSource } from '../../../__generated__/graphql/api';

export type SummaryRow = { label: string; value: string; tone?: 'warning' | 'muted' };

type OpeningDraft = Partial<
  Pick<OpenAccountFormData, 'kyc' | 'card_plan' | 'applicants' | 'credit_limit' | 'initial_apr'>
> & { programName?: string };

const NOT_CHOSEN = 'Not chosen';

const KYC_ROW: Record<KycSource, Omit<SummaryRow, 'label'>> = {
  [KycSource.Screen]: { value: 'The vendor screens each applicant. The account boards once they are approved.' },
  [KycSource.Attest]: { value: 'Skipped. You vouch for each applicant.', tone: 'warning' },
};

const IMPORT_VALUE_BY_COUNT = new Map([
  [0, 'No card. The account boards without one.'],
  [1, '1 i2c card is imported.'],
]);

const CARDS_VALUE_BY_PLAN: Record<CardPlan, (applicants: ApplicantFormData[]) => string> = {
  [CardPlan.Issue]: (applicants) =>
    applicants.length === 1 ? 'A new Lithic card is issued.' : `${applicants.length} new Lithic cards are issued.`,
  [CardPlan.Import]: (applicants) => {
    const imported = applicants.filter((applicant) => applicant.card_ref?.trim()).length;

    return IMPORT_VALUE_BY_COUNT.get(imported) ?? `${imported} i2c cards are imported.`;
  },
};

const applicantName = (applicant: ApplicantFormData, index: number) =>
  [applicant.first_name, applicant.last_name].filter(Boolean).join(' ').trim() || `Applicant ${index + 1}`;

const creditLimitRow = (creditLimit: number | undefined, initialApr: string | undefined): SummaryRow => {
  const amount = creditLimit ? centsToFormattedDollars(creditLimit) : undefined;

  if (!amount) return { label: 'Credit limit', value: 'Not set', tone: 'muted' };

  return { label: 'Credit limit', value: initialApr?.trim() ? `${amount} at ${initialApr.trim()}% APR` : amount };
};

export const summarizeOpening = ({
  programName,
  kyc,
  card_plan,
  applicants = [],
  credit_limit,
  initial_apr,
}: OpeningDraft): SummaryRow[] => [
  programName ? { label: 'Program', value: programName } : { label: 'Program', value: NOT_CHOSEN, tone: 'muted' },
  kyc ? { label: 'KYC', ...KYC_ROW[kyc] } : { label: 'KYC', value: NOT_CHOSEN, tone: 'muted' },
  card_plan
    ? { label: 'Cards', value: CARDS_VALUE_BY_PLAN[card_plan](applicants) }
    : { label: 'Cards', value: NOT_CHOSEN, tone: 'muted' },
  { label: 'Applicants', value: applicants.map(applicantName).join(' and ') },
  creditLimitRow(credit_limit, initial_apr),
];
